(() => {
  'use strict';

  const PREFIX = 'orivion-welcome-v1';
  const DAY = 86400000;
  const read = (storageName, key) => {
    try { return window[storageName].getItem(`${PREFIX}:${key}`); } catch (_) { return null; }
  };
  const write = (storageName, key, value) => {
    try { window[storageName].setItem(`${PREFIX}:${key}`, String(value)); } catch (_) {}
  };
  const remove = (storageName, key) => {
    try { window[storageName].removeItem(`${PREFIX}:${key}`); } catch (_) {}
  };
  const isRecent = (value, duration) => {
    const time = Number(value);
    return time > 0 && Date.now() >= time && Date.now() - time < duration;
  };
  const readPending = () => {
    let raw;
    try { raw = window.sessionStorage.getItem(`${PREFIX}:pending`); }
    catch (_) { return { pending: null, storageBlocked: true }; }
    try {
      const pending = JSON.parse(raw);
      return { pending: pending && typeof pending === 'object' ? pending : null, storageBlocked: false };
    } catch (_) { return { pending: null, storageBlocked: false }; }
  };

  const init = (dialog) => {
    if (dialog.dataset.welcomeInitialized === 'true' || typeof dialog.showModal !== 'function') return;
    dialog.dataset.welcomeInitialized = 'true';

    const form = dialog.querySelector('form');
    const heading = dialog.querySelector('.orivion-welcome__heading');
    const consent = dialog.querySelector('[data-welcome-consent]');
    const result = dialog.querySelector('[data-welcome-result]')?.dataset.welcomeResult;
    const returnValue = form?.querySelector('input[name="return_to"]')?.value;
    let returnTarget = null;
    try {
      const candidate = new URL(returnValue || '', window.location.href);
      if (returnValue && candidate.origin === window.location.origin && candidate.hash === `#${form.id}`) returnTarget = candidate;
    } catch (_) {}
    const { pending, storageBlocked } = readPending();
    const matchingPage = Boolean(returnTarget && returnTarget.origin === window.location.origin && returnTarget.pathname === window.location.pathname);
    const matchingFragment = matchingPage && returnTarget.hash === window.location.hash;
    const matchingPending = Boolean(
      pending && matchingPage && pending.formId === form.id &&
      isRecent(pending.submittedAt, 30 * 60 * 1000) &&
      pending.origin === returnTarget.origin && pending.returnPath === returnTarget.pathname &&
      pending.returnHash === returnTarget.hash
    );
    // A fresh exact-form marker tolerates a stripped fragment, never a different
    // form's fragment. If storage is blocked, require the exact same-page form
    // fragment instead. Query strings alone never associate a server result.
    const submittedHere = Boolean(
      (matchingPending && (!window.location.hash || matchingFragment)) ||
      (storageBlocked && matchingFragment)
    );
    const serverSuccess = result === 'success' && submittedHere;
    let previousFocus = null;
    let timer = null;
    let isClosing = false;
    let stopped = false;
    let previousOverflow = '';

    const focusable = () => [...dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]')]
      .filter((element) => element.type !== 'hidden' && element.getClientRects().length > 0);

    const close = (remember = true) => {
      if (isClosing || stopped) return;
      isClosing = true;
      stopped = true;
      window.clearTimeout(timer);
      remove('sessionStorage', 'pending');
      if (remember && !serverSuccess) write('localStorage', 'dismissed', Date.now());
      if (dialog.open) dialog.close();
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected && typeof previousFocus.focus === 'function') {
        previousFocus.focus({ preventScroll: true });
      }
      isClosing = false;
    };

    const open = () => {
      if (stopped || !dialog.isConnected || dialog.open || document.visibilityState === 'hidden') return;
      // Do not cover another modal or the mobile navigation.
      if (document.querySelector('dialog[open], [data-mobile-menu].is-open')) {
        timer = window.setTimeout(open, 1500);
        return;
      }
      previousFocus = document.activeElement;
      previousOverflow = document.body.style.overflow;
      dialog.showModal();
      document.body.style.overflow = 'hidden';
      write('sessionStorage', 'shown', Date.now());
      heading?.focus({ preventScroll: true });
    };

    dialog.querySelectorAll('[data-welcome-close]').forEach((button) => button.addEventListener('click', () => close()));
    dialog.querySelector('[data-welcome-shop]')?.addEventListener('click', () => close(false));
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      close();
    });
    dialog.addEventListener('close', () => {
      if (!isClosing) close();
    });
    dialog.addEventListener('click', (event) => {
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) close();
    });
    dialog.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const elements = focusable();
      if (!elements.length) { event.preventDefault(); heading?.focus(); return; }
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === heading)) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) {
        event.preventDefault(); first.focus();
      }
    });
    form?.addEventListener('submit', (event) => {
      if (!consent?.checked || !form.checkValidity()) {
        event.preventDefault();
        remove('sessionStorage', 'pending');
        form.reportValidity();
        return;
      }
      // Keep Shopify's normal form post and challenge handling. Never mark a click as a signup.
      if (returnTarget) {
        write('sessionStorage', 'pending', JSON.stringify({
          formId: form.id,
          origin: returnTarget.origin,
          returnPath: returnTarget.pathname,
          returnHash: returnTarget.hash,
          submittedAt: Date.now()
        }));
      } else remove('sessionStorage', 'pending');
      form.setAttribute('aria-busy', 'true');
    });
    form?.addEventListener('invalid', () => remove('sessionStorage', 'pending'), true);
    document.addEventListener('submit', (event) => {
      if (event.target !== form) remove('sessionStorage', 'pending');
    }, true);
    window.addEventListener('pageshow', (event) => {
      form?.removeAttribute('aria-busy');
      // A browser-back restore after a cancelled challenge is not a successful post.
      if (event.persisted && !result) remove('sessionStorage', 'pending');
    });
    document.addEventListener('shopify:section:unload', (event) => {
      if (event.target.contains(dialog)) close(false);
    });

    // Show this submission's result before suppressing an already-subscribed
    // customer: the successful post can itself have just changed that state.
    if (serverSuccess) {
      write('localStorage', 'subscribed', Date.now());
      remove('sessionStorage', 'pending');
      open();
      return;
    }
    if (dialog.dataset.customerSubscribed === 'true') {
      write('localStorage', 'subscribed', Date.now());
      remove('sessionStorage', 'pending');
      return;
    }
    if (result === 'error' && submittedHere) {
      remove('sessionStorage', 'pending');
      open();
      return;
    }
    // Clear stale, cancelled, mismatched or unrelated form returns, including
    // older timestamp-only markers. Never reuse one for a later footer signup.
    remove('sessionStorage', 'pending');
    // A different newsletter form's result must not unlock this offer or show
    // its validation errors inside this popup.
    if (result) return;
    if (isRecent(read('localStorage', 'subscribed'), 90 * DAY) || isRecent(read('localStorage', 'dismissed'), 14 * DAY) || read('sessionStorage', 'shown')) return;

    const delay = Math.max(5, Math.min(30, Number(dialog.dataset.delaySeconds) || 10)) * 1000;
    const eligibleAt = Date.now() + delay;
    const schedule = () => {
      window.clearTimeout(timer);
      if (!stopped && document.visibilityState !== 'hidden') timer = window.setTimeout(open, Math.max(0, eligibleAt - Date.now()));
    };
    document.addEventListener('visibilitychange', schedule);
    schedule();
  };

  const scan = (scope = document) => scope.querySelectorAll('[data-orivion-welcome]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  else scan();

  if (!window.__orivionWelcomeEditorListener) {
    document.addEventListener('shopify:section:load', (event) => scan(event.target));
    window.__orivionWelcomeEditorListener = true;
  }
})();
